import React, { useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { HiArrowRight, HiOutlineEmojiSad, HiOutlineRefresh, HiOutlineShieldCheck, HiOutlineTruck, HiOutlineGift, HiOutlineChatAlt2, HiOutlineSparkles } from 'react-icons/hi'
import { GiAmpleDress, GiTShirt, GiBabyBottle } from 'react-icons/gi'
import { FaWhatsapp } from 'react-icons/fa'
import ItemService from '../service/ItemService'
import brand, { whatsappLink } from '../brand'
import ProductCard from '../components/ProductCard'
import Skeleton, { ProductCardSkeleton } from '../components/Skeleton'
import EmptyState from '../components/EmptyState'
import { AGES, allItemsLink } from '../util/shopFilters'
import useReveal from '../util/useReveal'
import '../css/landingpage.css'

const FRESH_PICKS = 8

// brand.json home.perks[].icon -> icon
const PERK_ICONS = {
    exchange: HiOutlineRefresh,
    secure: HiOutlineShieldCheck,
    delivery: HiOutlineTruck,
    gift: HiOutlineGift,
    chat: HiOutlineChatAlt2
}

const AUDIENCES = [
    { key: 'girls', label: 'Girls', text: 'Pretty styles for her', to: allItemsLink({ for: 'girls' }), Icon: GiAmpleDress },
    { key: 'boys', label: 'Boys', text: 'Cool styles for him', to: allItemsLink({ for: 'boys' }), Icon: GiTShirt },
    { key: 'baby', label: 'Baby', text: 'Little ones, 0–1 year', to: allItemsLink({ age: '1' }), Icon: GiBabyBottle }
]

const Star = ({ className }) => (
    <svg className={className} viewBox='0 0 24 24' aria-hidden='true'>
        <path d='M12 2l2.6 6.9L22 9.3l-5.7 4.6 2 7.1L12 17l-6.3 4 2-7.1L2 9.3l7.4-.4z' />
    </svg>
)

const LandingPage = () => {

    const history = useHistory()
    const perks = (brand.home && brand.home.perks) || []

    const [categories, setCategories] = useState([])
    const [subList, setSubList] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)

    const [picks, setPicks] = useState([])
    const [picksLoading, setPicksLoading] = useState(true)

    const loadCategories = () => {
        setIsLoading(true)
        setLoadError(false)
        ItemService.getShopCategoriesPublic().then(response => {
            if (response.status) {
                setCategories(response.data.data.mainCategory || [])
                setSubList(response.data.data.subDisplayList || [])
                setIsLoading(false)
            }
        }).catch(error => {
            console.log(error)
            setLoadError(true)
            setIsLoading(false)
        });
    }

    const loadPicks = () => {
        setPicksLoading(true)
        // newest arrivals: one small page of the storefront feed
        ItemService.getItemFeed({ size: FRESH_PICKS }).then(response => {
            setPicks(((response.data && response.data.items) || []).slice(0, FRESH_PICKS))
            setPicksLoading(false)
        }).catch(error => {
            // Fresh picks are a bonus section: on failure it simply hides
            console.log(error)
            setPicks([])
            setPicksLoading(false)
        });
    }

    useEffect(() => {
        loadCategories()
        loadPicks()
    }, []);

    useReveal([isLoading, picksLoading])

    const openItem = (id) => history.push({ pathname: '/shop/items/view/' + id, state: { id } })
    const openCategory = (id) => history.push({ pathname: '/category/' + id })

    const firstPerk = perks[0]
    const FirstPerkIcon = firstPerk && (PERK_ICONS[firstPerk.icon] || HiOutlineSparkles)

    return (
        <main className='mv-page mv-landing'>

            {/* ---------- Hero ---------- */}
            <section className={'mv-hero' + (brand.heroImage ? '' : ' mv-hero-text-only')}>
                <div className='mv-hero-decor' aria-hidden='true'>
                    <span className='mv-blob mv-blob-1' />
                    <span className='mv-blob mv-blob-2' />
                    <span className='mv-blob mv-blob-3' />
                    <Star className='mv-hero-star mv-hero-star-1' />
                    <Star className='mv-hero-star mv-hero-star-2' />
                    <Star className='mv-hero-star mv-hero-star-3' />
                </div>

                <div className='mv-hero-text'>
                    <span className='mv-hero-eyebrow'><HiOutlineSparkles aria-hidden='true' /> {brand.hero.eyebrow}</span>
                    <h1 className='mv-hero-title'>
                        {brand.hero.title}
                        {brand.hero.titleEmphasis && <>
                            {' '}
                            <span className='mv-hero-em'>
                                {brand.hero.titleEmphasis}
                                <svg className='mv-hero-squiggle' viewBox='0 0 200 14' preserveAspectRatio='none' aria-hidden='true'>
                                    <path d='M2 9 C 30 2, 50 13, 80 7 S 130 1, 160 8 S 190 10, 198 5' />
                                </svg>
                            </span>
                        </>}
                    </h1>
                    <p className='mv-hero-sub'>{brand.hero.text}</p>
                    <div className='mv-hero-ctas'>
                        <Link className='mv-btn mv-btn-primary' to={allItemsLink({ for: 'girls' })}>Shop Girls</Link>
                        <Link className='mv-btn mv-btn-secondary' to={allItemsLink({ for: 'boys' })}>Shop Boys</Link>
                    </div>
                    <Link className='mv-hero-all' to='/items/all'>or browse everything <HiArrowRight aria-hidden='true' /></Link>
                </div>

                {brand.heroImage &&
                    <div className='mv-hero-media'>
                        <div className='mv-hero-frame'>
                            <img src={brand.heroImage} alt={brand.hero.imageAlt || ''} />
                        </div>
                        {firstPerk &&
                            <span className='mv-hero-sticker'>
                                <FirstPerkIcon aria-hidden='true' /> {firstPerk.title}
                            </span>}
                    </div>}
            </section>

            {/* ---------- Promises ---------- */}
            {perks.length > 0 &&
                <ul className='mv-perks' aria-label={`Why parents shop at ${brand.name}`}>
                    {perks.map(perk => {
                        const Icon = PERK_ICONS[perk.icon] || HiOutlineSparkles
                        return (
                            <li key={perk.title} className='mv-perk'>
                                <span className='mv-perk-icon'><Icon aria-hidden='true' /></span>
                                <span className='mv-perk-text'>
                                    <strong>{perk.title}</strong>
                                    {perk.text && <small>{perk.text}</small>}
                                </span>
                            </li>
                        )
                    })}
                </ul>}

            {/* ---------- Who are we shopping for ---------- */}
            <section className='mv-section mv-reveal' aria-labelledby='shop-for'>
                <div className='mv-section-head'>
                    <h2 id='shop-for' className='mv-section-title'>Who are we shopping for?</h2>
                </div>
                <div className='mv-audience-grid'>
                    {AUDIENCES.map(({ key, label, text, to, Icon }) =>
                        <Link key={key} to={to} className={'mv-audience mv-audience-' + key}>
                            <span className='mv-audience-icon'><Icon aria-hidden='true' /></span>
                            <span className='mv-audience-text'>
                                <strong>{label}</strong>
                                <small>{text}</small>
                            </span>
                            <HiArrowRight className='mv-audience-arrow' aria-hidden='true' />
                        </Link>
                    )}
                </div>
            </section>

            {/* ---------- Shop by age ---------- */}
            <section className='mv-section mv-reveal' aria-labelledby='shop-by-age'>
                <div className='mv-section-head'>
                    <h2 id='shop-by-age' className='mv-section-title'>Shop by age</h2>
                </div>
                <div className='mv-age-row'>
                    {AGES.slice(1).map(age => {
                        const n = Number(age.value)
                        return (
                            <Link key={age.value} to={allItemsLink({ age: age.value })} className='mv-age' aria-label={`Age ${age.label}`}>
                                <span className='mv-age-num'>{n - 1}–{n}</span>
                                <span className='mv-age-unit'>{n === 1 ? 'year' : 'years'}</span>
                            </Link>
                        )
                    })}
                </div>
            </section>

            {loadError &&
                <EmptyState
                    icon={<HiOutlineEmojiSad />}
                    title='We could not load the shop'
                    text='Please check your connection and try again.'
                    actionLabel='Try again'
                    onAction={loadCategories}
                />
            }

            {/* ---------- Categories ---------- */}
            {(isLoading || categories.length > 0) &&
                <section className='mv-section mv-reveal' aria-labelledby='shop-by-category'>
                    <div className='mv-section-head'>
                        <h2 id='shop-by-category' className='mv-section-title'>Shop by category</h2>
                    </div>
                    <div className={'mv-cat-grid' + (categories.length % 2 === 1 ? ' has-feature' : '')}>
                        {isLoading ?
                            Array.from({ length: 4 }, (_, i) => (
                                <Skeleton key={i} className='mv-cat-tile mv-cat-skeleton' height='auto' />
                            ))
                            :
                            categories.map((cat, index) =>
                                <button type='button' key={cat.categoryId} className='mv-cat-tile' onClick={() => openCategory(cat.categoryId)}>
                                    {/* the wide first tile (odd count, phones) uses the category's 16:9 photo */}
                                    {index === 0 && categories.length % 2 === 1 && cat.imageWide ?
                                        <picture>
                                            <source media='(max-width: 767px)' srcSet={cat.imageWide} />
                                            <img src={cat.image} alt='' loading='lazy' />
                                        </picture> :
                                        <img src={cat.image} alt='' loading='lazy' />}
                                    <span className='mv-cat-label'>
                                        <span className='mv-cat-name'>{cat.categoryName}</span>
                                        <span className='mv-cat-go'>Shop now <HiArrowRight aria-hidden='true' /></span>
                                    </span>
                                </button>
                            )
                        }
                    </div>
                </section>
            }

            {/* ---------- Fresh picks (with prices) ---------- */}
            {(picksLoading || picks.length > 0) &&
                <section className='mv-section mv-reveal' aria-labelledby='fresh-picks'>
                    <div className='mv-section-head'>
                        <div>
                            <h2 id='fresh-picks' className='mv-section-title'>Fresh picks</h2>
                            <p className='mv-section-sub'>Handpicked styles your little one will love</p>
                        </div>
                        <Link className='mv-pill-link' to='/items/all'>View all <HiArrowRight aria-hidden='true' /></Link>
                    </div>
                    <div className='mv-product-grid'>
                        {picksLoading ?
                            Array.from({ length: 4 }, (_, i) => <ProductCardSkeleton key={i} />)
                            :
                            picks.map(item =>
                                <ProductCard
                                    key={item.itemId}
                                    name={item.itemName}
                                    image={item.listImageUrl || item.imageUrl}
                                    meta={item.sizes}
                                    mrp={item.mrp}
                                    finalPrice={item.finalPrice}
                                    onClick={() => openItem(item.itemId)}
                                />
                            )
                        }
                    </div>
                    {!picksLoading &&
                        <div className='mv-landing-more'>
                            <Link className='mv-btn mv-btn-secondary' to='/items/all'>View all products <HiArrowRight aria-hidden='true' /></Link>
                        </div>}
                </section>
            }

            {/* ---------- Curated collections ---------- */}
            {isLoading &&
                <section className='mv-section' aria-hidden='true'>
                    <Skeleton width='180px' height={24} style={{ marginBottom: 12 }} />
                    <div className='mv-rail'>
                        {Array.from({ length: 5 }, (_, i) => <ProductCardSkeleton key={i} />)}
                    </div>
                </section>
            }

            {subList.map(cat =>
                <section className='mv-section mv-reveal' key={cat.categoryId} aria-label={cat.categoryName}>
                    <div className='mv-section-head'>
                        <h2 className='mv-section-title'>{cat.categoryName}</h2>
                        <button type='button' className='mv-pill-link' onClick={() => openCategory(cat.categoryId)}>
                            View all <HiArrowRight aria-hidden='true' />
                        </button>
                    </div>
                    <div className='mv-rail'>
                        {(cat.subList || []).map(item =>
                            <ProductCard
                                key={item.itemId}
                                name={item.itemName}
                                image={item.itemImage}
                                onClick={() => openItem(item.itemId)}
                            />
                        )}
                    </div>
                </section>
            )}

            {/* ---------- Help ---------- */}
            {brand.contact && brand.contact.whatsapp &&
                <section className='mv-help mv-reveal' aria-labelledby='size-help'>
                    <span className='mv-help-icon' aria-hidden='true'><HiOutlineChatAlt2 /></span>
                    <div className='mv-help-text'>
                        <h2 id='size-help'>Not sure about the size?</h2>
                        <p>Send us a message on WhatsApp and we'll help you pick the right fit.</p>
                    </div>
                    <a className='mv-btn mv-help-btn'
                        href={whatsappLink(`Hi ${brand.name}, can you help me choose a size?`)}
                        target='_blank' rel='noopener noreferrer'>
                        <FaWhatsapp aria-hidden='true' /> Chat on WhatsApp
                    </a>
                </section>}
        </main>
    )
}

export default LandingPage
